// Assign / re-assign a physical barcode sticker to an asset.
// Backend endpoint returns 409 when the barcode is already linked to a different asset — the
// error message includes the other asset's display_code and instructs the user to unlink first.

import React, { useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Field, FieldRow } from '../components/ui/Field';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { ScanInput } from '../components/BarcodeScanner';

interface Props {
  assetId: string;
  displayCode: string;
  currentBarcode: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AssignBarcodeModal: React.FC<Props> = ({ assetId, displayCode, currentBarcode, onClose, onSuccess }) => {
  const api = useApi();
  const toast = useToast();
  const [barcode, setBarcode] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // overrideValue lets a fresh scan submit immediately without waiting for the
  // `barcode` state to flush — ScanInput passes the just-scanned text directly.
  const handleSubmit = async (overrideValue?: string) => {
    const value = (overrideValue ?? barcode).trim();
    if (!value) { toast.error('Barcode cannot be empty'); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await api.post(`/assets/${assetId}/assign-barcode`, {
        body: { barcode: value, reason: reason.trim() || undefined },
        silent: true,
      });
      if (result.unchanged) {
        toast.success('Barcode already linked, no change');
      } else {
        toast.success(currentBarcode ? 'Barcode re-assigned' : 'Barcode linked');
      }
      onSuccess();
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e.status === 409) {
        toast.error(e.message ?? 'Barcode already linked to another asset');
      } else {
        toast.error('Failed to assign barcode');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={currentBarcode ? 'Re-assign Barcode' : 'Assign Barcode'}
      meta={`${displayCode}${currentBarcode ? `, current: ${currentBarcode}` : ''}`}
      size="md"
      dirty={barcode.trim() !== '' || reason.trim() !== ''}
      footer={
        <>
          <button onClick={onClose} className={buttonClass('quiet', 'md')}>Cancel</button>
          <button
            onClick={() => handleSubmit()}
            disabled={submitting}
            className={buttonClass('filled', 'md')}
          >
            {submitting ? 'Linking…' : 'Link Barcode'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FieldRow label="Barcode">
          <ScanInput
            value={barcode}
            onChange={setBarcode}
            onSubmit={handleSubmit}
            autoSubmitOnScan
            autoFocus
            disabled={submitting}
            placeholder="Scan or type sticker value"
            scanTitle={currentBarcode ? 'Scan New Barcode' : 'Scan Barcode'}
            scanHint={`Point camera at the sticker on ${displayCode}`}
          />
        </FieldRow>
        <FieldRow label="Reason (optional)">
          <Field
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. sticker damaged, replacement"
          />
        </FieldRow>
      </div>
    </Dialog>
  );
};

export default AssignBarcodeModal;
